import React from "react";
import {
  SocialMediaReport,
  ReportComparison,
  ReportMeta,
  TrendDataPoint,
} from "../hooks/useSocialMediaReport";
import {
  ChangePill,
  ReportHeadline,
  ReportPanel,
  ReportPanelEmpty,
  ReportPanelNote,
  ReportStat,
  ReportStats,
} from "./ReportPanel";
import {
  BarList,
  QuietList,
  SOURCE_COLOR,
  SourceBadge,
  Sparkline,
  SplitBar,
  VizHeading,
} from "./ReportViz";
import { proRataLabel } from "../utils/metricChange";
import { HeartIcon, LinkIcon, RefreshIcon, CheckIcon, ErrorIcon, VideoIcon } from "@/components/icons";
import { PlatformConfig } from "@/config/platforms";
import { formatRelativeTime } from "@/utils/date";

type HealthStatus = "fresh" | "stale" | "none";

function getHealth(platform: string, meta: ReportMeta | undefined, data: unknown): HealthStatus {
  if (!data) return "none";
  const keyMap: Record<string, keyof ReportMeta> = {
    facebook: "facebookFetchedAt",
    instagram: "instagramFetchedAt",
    tiktok: "tiktokFetchedAt",
  };
  const fetchedAt = meta?.[keyMap[platform] ?? "updatedAt"];
  if (!fetchedAt) return "stale";
  const age = Date.now() - new Date(fetchedAt).getTime();
  return age > 7 * 24 * 60 * 60 * 1000 ? "stale" : "fresh";
}

function formatFetchedAt(iso: string | null | undefined): string | null {
  return formatRelativeTime(iso);
}

const HealthBadge: React.FC<{ status: HealthStatus; fetchedAt?: string | null }> = ({ status, fetchedAt }) => {
  const label = formatFetchedAt(fetchedAt);
  if (status === "fresh") {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-green-600">
        <CheckIcon size={10} className="text-green-500" />
        {label || "Fresh"}
      </span>
    );
  }
  if (status === "stale") {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-amber-600">
        <ErrorIcon size={10} className="text-amber-500" />
        {label || "Stale"}
      </span>
    );
  }
  return <span className="text-xs text-gray-400">No data</span>;
};

interface PlatformCardProps {
  platformKey: string;
  config: PlatformConfig;
  report: SocialMediaReport;
  comparison?: ReportComparison | null;
  /** Months of the trend window, for the folded followers sparkline. */
  historicalData: TrendDataPoint[];
  onRefresh?: (platform: "facebook" | "instagram" | "tiktok") => void;
  isRefreshing?: boolean;
  disabled?: boolean;
}

export const PlatformCard: React.FC<PlatformCardProps> = ({
  platformKey,
  config,
  report,
  comparison,
  historicalData,
  onRefresh,
  isRefreshing = false,
  disabled = false,
}) => {
  const icon = (
    <SourceBadge color={config.color}>
      <config.iconComponent className="w-3.5 h-3.5" />
    </SourceBadge>
  );
  const data = report[platformKey as keyof SocialMediaReport] as Record<string, number> | null;
  if (!data) {
    return (
      <ReportPanel
        title={config.label}
        icon={icon}
        collapsedBody={<ReportPanelNote>{`No ${config.label} data for this month.`}</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message={`No ${config.label} data for this month.`}
          hint="Connect the account or refresh the report to pull its metrics."
        />
      </ReportPanel>
    );
  }

  const platformChanges = comparison?.changes[platformKey as keyof typeof comparison.changes] as
    | Record<string, { value: number; percentage: number } | null>
    | null
    | undefined;

  const fetchedAtKeyMap: Record<string, keyof ReportMeta> = {
    facebook: "facebookFetchedAt",
    instagram: "instagramFetchedAt",
    tiktok: "tiktokFetchedAt",
  };
  const status = getHealth(platformKey, report._meta, data);
  const fetchedAt = report._meta?.[fetchedAtKeyMap[platformKey] ?? "updatedAt"];

  const refreshButton = onRefresh ? (
    <button
      onClick={() => onRefresh(platformKey as "facebook" | "instagram" | "tiktok")}
      disabled={disabled}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      title={`Re-fetch ${config.label}`}
    >
      <RefreshIcon size={12} className={isRefreshing ? "animate-spin" : ""} />
      {isRefreshing ? "..." : "Refresh"}
    </button>
  ) : undefined;

  const impressions = data.totalImpressions ?? 0;
  const reach = data.totalReach ?? 0;
  const metrics: Array<{ title: string; value: number; change?: { value: number; percentage: number }; icon?: React.ReactNode; subtitle?: string }> = [];
  // FB impressions/reach come from page /insights, which needs the
  // read_insights permission. Without it they read 0 — hide the tiles
  // rather than show misleading zeros, matching the > 0 guards used for
  // the other FB tiles below. IG surfaces both. TikTok's Login Kit
  // exposes no post-level impressions/reach, so skip them entirely
  // rather than render a misleading 0 / 0.
  if (platformKey !== "tiktok" && (platformKey !== "facebook" || impressions > 0)) {
    metrics.push({ title: "Impressions", value: impressions, change: platformChanges?.impressions ?? undefined });
  }
  if (platformKey !== "tiktok" && (platformKey !== "facebook" || reach > 0)) {
    metrics.push({ title: "Reach", value: reach, change: platformChanges?.reach ?? undefined });
  }

  if (platformKey === "facebook") {
    const organic = data.totalOrganicImpressions ?? 0;
    const paid = data.totalPaidImpressions ?? 0;
    // Only surface the paid/organic split when we have data for it.
    // Older cached blobs predate these keys and would show 0 / 0.
    if (organic > 0 || paid > 0) {
      metrics.push({
        title: "Organic impressions",
        value: organic,
        subtitle: "Agency-driven",
      });
      metrics.push({
        title: "Paid impressions",
        value: paid,
        subtitle: "Boosted",
      });
    }
    if ((data.totalEngagedUsers ?? 0) > 0) {
      metrics.push({
        title: "Engaged Users",
        value: data.totalEngagedUsers,
        icon: <HeartIcon className="w-3.5 h-3.5 text-blue-500" />,
      });
    }
  }

  if (platformKey === "instagram") {
    if (data.totalInteractions > 0) {
      metrics.push({ title: "Engagement", value: data.totalInteractions, icon: <HeartIcon className="w-3.5 h-3.5 text-pink-500" /> });
    }
    // Profile views = consideration step in the funnel; sits between
    // reach and intent. Surfaced as its own tile so it doesn't fight
    // for axis space with link taps in the Booking Intent chart.
    if ((data.totalProfileViews ?? 0) > 0) {
      metrics.push({
        title: "Profile views",
        value: data.totalProfileViews,
        subtitle: "Consideration",
      });
    }
    // Both profile link taps (multi-link bio sticker) and website
    // clicks (Website CTA button) are intent signals. We surface the
    // raw counts without anointing one as "the" booking signal — the
    // section header already establishes the category.
    if ((data.totalProfileLinksTaps ?? 0) > 0) {
      metrics.push({
        title: "Profile link taps",
        value: data.totalProfileLinksTaps,
        icon: <LinkIcon className="w-3.5 h-3.5 text-pink-500" />,
      });
    }
    if (data.totalWebsiteClicks > 0) {
      metrics.push({ title: "Website clicks", value: data.totalWebsiteClicks, icon: <LinkIcon className="w-3.5 h-3.5 text-blue-500" /> });
    }
  }

  if (platformKey === "tiktok") {
    // Login Kit's user.info exposes lifetime account totals, not
    // per-month insights. Surface Likes and Videos as their own tiles;
    // followers live in the Followers panel.
    metrics.push({
      title: "Likes",
      value: data.totalLikes ?? 0,
      icon: <HeartIcon className="w-3.5 h-3.5 text-gray-700" />,
    });
    metrics.push({
      title: "Videos",
      value: data.totalVideos ?? 0,
      icon: <VideoIcon className="w-3.5 h-3.5 text-gray-700" />,
    });
  }

  const engagementItems: Array<{ title: string; value: number }> = [];
  if (platformKey === "instagram") {
    if (data.totalLikes > 0) engagementItems.push({ title: "Likes", value: data.totalLikes });
    if (data.totalComments > 0) engagementItems.push({ title: "Comments", value: data.totalComments });
    if (data.totalShares > 0) engagementItems.push({ title: "Shares", value: data.totalShares });
    if (data.totalSaved > 0) engagementItems.push({ title: "Saved", value: data.totalSaved });
    if ((data.totalReplies ?? 0) > 0) engagementItems.push({ title: "DMs", value: data.totalReplies });
  }
  if (platformKey === "facebook") {
    const r = data as Record<string, number | undefined>;
    if ((r.totalReactionsLike ?? 0) > 0) engagementItems.push({ title: "Likes", value: r.totalReactionsLike ?? 0 });
    if ((r.totalReactionsLove ?? 0) > 0) engagementItems.push({ title: "Loves", value: r.totalReactionsLove ?? 0 });
    if ((r.totalReactionsWow ?? 0) > 0) engagementItems.push({ title: "Wows", value: r.totalReactionsWow ?? 0 });
    if ((r.totalReactionsHaha ?? 0) > 0) engagementItems.push({ title: "Hahas", value: r.totalReactionsHaha ?? 0 });
    if ((r.totalVideoViews ?? 0) > 0) engagementItems.push({ title: "Video views", value: r.totalVideoViews ?? 0 });
  }

  const followerChange =
    platformKey === "facebook"
      ? platformChanges?.fans
      : platformKey === "instagram"
      ? platformChanges?.followers
      : undefined;
  const followers = (data[config.followersKey] ?? 0).toLocaleString();
  // Folded: followers and their line by month; a month with no count is a gap.
  const collapsedBody = (
    <ReportHeadline
      stat={
        <ReportStat
          size="lg"
          label="Followers"
          value={followers}
          change={followerChange ?? undefined}
        />
      }
      chart={
        <Sparkline
          values={historicalData.map((d) => {
            const count = d.followers?.[platformKey as keyof NonNullable<typeof d.followers>];
            return count ? count : null;
          })}
          color={config.color}
          title={`${config.label} followers by month`}
        />
      }
    />
  );
  // Open: followers and the first two flows as key figures; Facebook's
  // organic and paid impressions as one split bar, paid apart in amber;
  // the rest demoted to a quiet list, and engagement by type as bars.
  const splitTitles = ["Organic impressions", "Paid impressions"];
  const organic = metrics.find((m) => m.title === splitTitles[0]);
  const paid = metrics.find((m) => m.title === splitTitles[1]);
  const keyMetrics = metrics.slice(0, 2);
  const quietMetrics = metrics.slice(2).filter((m) => !splitTitles.includes(m.title));

  return (
    <ReportPanel
      title={config.label}
      icon={icon}
      collapsedBody={collapsedBody}
      headlineLabel="Followers"
      summary={
        <>
          <HealthBadge status={status} fetchedAt={fetchedAt} />
          {platformChanges && comparison?.proRata && (
            <span className="text-xs text-gray-400"> · {proRataLabel(comparison.proRata)}</span>
          )}
        </>
      }
      actions={refreshButton}
    >
      <div className="space-y-5">
        <ReportStats>
          <ReportStat label="Followers" value={followers} change={followerChange ?? undefined} />
          {keyMetrics.map((m) => (
            <ReportStat key={m.title} label={m.title} value={m.value.toLocaleString()} change={m.change} />
          ))}
        </ReportStats>
        {organic && paid && (
          <div>
            <VizHeading>Impressions, organic vs paid</VizHeading>
            <SplitBar
              parts={[
                { label: "Organic", value: organic.value, color: config.color },
                { label: "Paid", value: paid.value, color: SOURCE_COLOR.paid },
              ]}
            />
          </div>
        )}
        {quietMetrics.length > 0 && (
          <QuietList
            rows={quietMetrics.map((m) => ({
              label: m.subtitle ? `${m.title} · ${m.subtitle.toLowerCase()}` : m.title,
              value: m.value.toLocaleString(),
              aside: m.change ? <ChangePill change={m.change} /> : undefined,
            }))}
          />
        )}
        {engagementItems.length > 0 && (
          <div>
            <VizHeading>Engagement by type</VizHeading>
            <BarList
              color={config.color}
              rows={engagementItems.map((e) => ({
                key: e.title,
                label: e.title,
                value: e.value,
                display: e.value.toLocaleString(),
              }))}
            />
          </div>
        )}
      </div>
    </ReportPanel>
  );
};
